import { motion } from 'motion/react'
import { EASE_OUT } from '../../lib/motion'
import { STATUS_COPY } from '../../copy/status'
import { TOKENS } from './dashboard-primitives'

/**
 * The quiet line a loading surface shows when the server has refused its request
 * as busy twice and the app is asking again by itself (#392, owner ruling
 * CAP-O26, option B). Drawn by `PanelSkeleton`, above the skeleton's shapes, so
 * it sits on the surface that asked, and by `RequireFirstRun` while it waits on
 * the page's first request; `useBusyLine` decides whether it is drawn.
 *
 * Calm is the brief, as it is for `PanelError`: the same terracotta wash over the
 * same `--warm-dark` fill (a wash is how this app separates things, never a
 * rule or a border), and none of what would say "stop": no icon, no pulse, no
 * counter, no button. It asks nothing of the reader, because the app is already
 * doing the only thing there is to do. It is quieter than the error note it
 * gives way to: no eyebrow label, no dot, one line of body type at the note's
 * own `--cream-soft` (the note's prose tier: it is information the reader
 * needs, not a footnote). It is as wide as its sentence and no wider
 * (`w-fit`): spread across a 1,000 px column the same wash read as a banner,
 * which is the weight the owner's "thin, calm line" is not (design review,
 * seen on the real stack).
 *
 * It appears at most a few times a day, and only in an overload, so it is
 * allowed one gentle entrance: an opacity fade (200ms, the app's strong
 * ease-out), with no movement at all, so `prefers-reduced-motion` has nothing
 * to remove. It has no exit: it leaves with the skeleton it sits in, which is
 * replaced by the content or by the error note at the same moment.
 *
 * It is a polite part of the skeleton's status region, so a screen reader hears
 * it when it arrives rather than only the first "Loading ..." announcement.
 * `data-busy-line` is what a test finds it by.
 */
export function BusyLine() {
  return (
    <motion.p
      data-busy-line
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2, ease: EASE_OUT }}
      className="mb-3 w-fit max-w-full rounded-xl px-3.5 py-2 text-[13px] leading-snug"
      style={{
        fontFamily: 'var(--font-body)',
        color: TOKENS.creamSoft,
        backgroundColor: TOKENS.warmDark,
        backgroundImage: `linear-gradient(${TOKENS.terracottaSoft}, ${TOKENS.terracottaSoft})`,
      }}
    >
      {STATUS_COPY.busyRetrying}
    </motion.p>
  )
}
